import type { FC } from "react";
import { ASSISTANT_AVATAR } from "@/theme/tokens";
import {
	type AssistantAvatarEyes,
	type AssistantAvatarFacialHair,
	type AssistantAvatarMood,
	AssistantHumanAccessory,
} from "@/ui/AssistantAvatar/assistantAvatarConfig";
import { HUMAN_FACE_TRANSFORM } from "@/ui/AssistantAvatar/assistantAvatarGeometry";
import { AvatarEyes } from "../shared/AvatarEyes";
import { AvatarGlasses } from "../shared/AvatarGlasses";
import { AvatarMouth } from "../shared/AvatarMouth";
import { AvatarShades } from "../shared/AvatarShades";
import { HumanBrows } from "./HumanBrows";
import { HumanFacialHair } from "./HumanFacialHair";

const { FACE, FEATURE_INK } = ASSISTANT_AVATAR;

interface HumanHeadProps {
	mood: AssistantAvatarMood;
	eyes: AssistantAvatarEyes;
	accessory: AssistantHumanAccessory;
	facialHair: AssistantAvatarFacialHair;
	skin: string;
	/** Brow and facial-hair color. */
	hairInk: string;
}

/** Ears, face and features. Eyes, mouth and eyewear reuse the character parts, scaled to a human face. */
export const HumanHead: FC<HumanHeadProps> = ({ mood, eyes, accessory, facialHair, skin, hairInk }) => (
	<>
		<circle cx={36} cy={62} r={5} fill={skin} />
		<circle cx={84} cy={62} r={5} fill={skin} />
		<ellipse cx={60} cy={58} rx={24} ry={27} fill={skin} />
		<ellipse cx={46} cy={70} rx={4.5} ry={2.6} fill={FACE.CHEEK_HUMAN} />
		<ellipse cx={74} cy={70} rx={4.5} ry={2.6} fill={FACE.CHEEK_HUMAN} />
		<HumanBrows mood={mood} color={hairInk} />
		<HumanFacialHair variant={facialHair} color={hairInk} />
		<g transform={HUMAN_FACE_TRANSFORM}>
			{accessory !== AssistantHumanAccessory.Shades && <AvatarEyes type={eyes} ink={FEATURE_INK.HUMAN} />}
			<AvatarMouth mood={mood} ink={FEATURE_INK.HUMAN} />
			{accessory === AssistantHumanAccessory.Glasses && <AvatarGlasses />}
			{accessory === AssistantHumanAccessory.Shades && <AvatarShades />}
		</g>
	</>
);
